import axios from 'axios';

import { optimisticTranslate } from '@i18n';

import {
    TransactionsErrorCode,
    TransactionState,
    TransactionType
} from '@framework/constants/enums/transaction';

import { type Transaction, type TransactionErrorHandler } from '@type/transaction';

const getPreApprovalValidator = (type: TransactionType) => {
    if ([TransactionType.ChangePhone, TransactionType.ChangeOneTouch].includes(type)) {
        return (transaction: Transaction) => {
            return (
                transaction.state === TransactionState.Pending &&
                // when pre-authentication is not required, it means that the transaction is already approved.
                !transaction.requiresPreAuthentication
            );
        };
    }
    return null;
};

export const getIsPreApprovalOperation = (transaction: Transaction) => {
    return !!getPreApprovalValidator(transaction.type);
};

export const getIsPreApprovalSucceeded = (transaction: Transaction) => {
    return !!getPreApprovalValidator(transaction.type)?.(transaction);
};

export const getTransactionThrottleErrorMessage = (wait?: number) => {
    const waitTimeInSeconds = wait ?? 0;
    const waitTimeInHours = (waitTimeInSeconds > 3600 && Math.floor(waitTimeInSeconds / 3600)) || 0;
    const waitTimeInMinutes = (waitTimeInSeconds > 60 && Math.floor(waitTimeInSeconds / 60)) || 0;
    if (waitTimeInHours) {
        return optimisticTranslate(
            'We have received too many requests. Please wait and try again in {{ hours }} hour(s).',
            { hours: waitTimeInHours }
        );
    }
    if (waitTimeInMinutes) {
        return optimisticTranslate(
            'We have received too many requests. Please wait and try again in {{ minutes }} minute(s).',
            { minutes: waitTimeInMinutes }
        );
    }
    return optimisticTranslate(
        'We have received too many requests. Please wait and try again in {{ seconds }} second(s).',
        { seconds: waitTimeInSeconds }
    );
};

export const getTransactionErrorTitleByCode = (
    error: TransactionsErrorCode
): string | undefined => {
    switch (error) {
        case TransactionsErrorCode.Throttled:
            return optimisticTranslate('Too many requests');
        case TransactionsErrorCode.TransactionExpired:
        case TransactionsErrorCode.ExpiredTransaction:
            return optimisticTranslate('Session expired');
        default:
            return optimisticTranslate('Something went wrong');
    }
};

export const getTransactionErrorMessageByCode = (
    code: TransactionsErrorCode,
    params?: { wait?: number }
): string => {
    const ERROR_MESSAGES_BY_CODE: { [key: string]: string } = {
        [TransactionsErrorCode.InvalidTransaction]: optimisticTranslate(
            'This transaction is invalid or expired and must be started again.'
        ),
        [TransactionsErrorCode.ImmutableTransaction]: optimisticTranslate(
            'This transaction may not be updated or changed.'
        ),
        [TransactionsErrorCode.InvalidApprovalMethod]: optimisticTranslate(
            'This approval method is invalid for the request.'
        ),
        [TransactionsErrorCode.ExpiredTransaction]: optimisticTranslate(
            'This transaction is expired and must be restarted.'
        ),
        [TransactionsErrorCode.ApprovalMethodRequired]: optimisticTranslate(
            'This transaction requires that it be approved using a second factor but no second factor is available for this user.'
        ),
        [TransactionsErrorCode.Throttled]: getTransactionThrottleErrorMessage(params?.wait)
    };

    return (
        ERROR_MESSAGES_BY_CODE[code] ||
        optimisticTranslate('Something went wrong. Please try again.')
    );
};

export const getIsTransactionFatalError = (code: TransactionsErrorCode): boolean => {
    return [
        TransactionsErrorCode.AccountInactive,
        TransactionsErrorCode.ApprovalMethodRequired,
        TransactionsErrorCode.AuthenticationError,
        TransactionsErrorCode.ExpiredInvitation,
        TransactionsErrorCode.ExpiredResetToken,
        TransactionsErrorCode.ExpiredTransaction,
        TransactionsErrorCode.ImmutableTransaction,
        TransactionsErrorCode.InvalidApprovalMethod,
        TransactionsErrorCode.InvalidTransaction,
        TransactionsErrorCode.InvitationInvalid,
        TransactionsErrorCode.RemovalRequestExpired,
        TransactionsErrorCode.RemovalRequestInProgress,
        TransactionsErrorCode.RemovalRequestInvalid,
        TransactionsErrorCode.RemovalRequestUnconfirmed,
        TransactionsErrorCode.TransactionExpired,
        TransactionsErrorCode.Throttled,
        TransactionsErrorCode.TransactionNotFound
    ].includes(code);
};

export const parseTransactionError = (error: unknown) => {
    if (!axios.isAxiosError(error)) {
        return {};
    }
    const code = error.response?.data?.code;
    const status = error.response?.status;
    const waitTimeInSeconds = error?.response?.data?.wait;
    return {
        code,
        status,
        waitTimeInSeconds
    };
};

export const transactionErrorHandler =
    (onError?: TransactionErrorHandler, onFatal?: () => void) =>
    (error: unknown, fatal?: boolean) => {
        if (typeof error === 'string') {
            onError?.({
                message: error,
                fatal: fatal ?? false
            });
            return;
        }
        const { code, waitTimeInSeconds } = parseTransactionError(error);
        if (!code) {
            onError?.({
                error,
                fatal: fatal ?? false
            });
            return;
        }
        const isFatalError = getIsTransactionFatalError(code);
        if (isFatalError) {
            onFatal?.();
        }
        onError?.({
            message: getTransactionErrorMessageByCode(code, {
                wait: waitTimeInSeconds
            }),
            error,
            fatal: isFatalError
        });
    };
